import type { Cell, Agent } from '../demo/seed';
import { GRID_SIZE } from '../demo/seed';
import { generateName, mockTonAddress } from '../demo/names';
import { GENESIS_PROMPTS } from '../demo/prompts';
import { cellIsAlive } from './survival';

export function emptyNeighbor(cells: Cell[], cell: Cell): Cell | null {
  const dirs: [number, number][] = [
    [-1, -1], [0, -1], [1, -1],
    [-1, 0], [1, 0],
    [-1, 1], [0, 1], [1, 1],
  ];
  const shuffled = [...dirs].sort(() => Math.random() - 0.5);
  for (const [dx, dy] of shuffled) {
    const nx = cell.x + dx;
    const ny = cell.y + dy;
    if (nx < 0 || ny < 0 || nx >= GRID_SIZE || ny >= GRID_SIZE) continue;
    const target = cells[ny * GRID_SIZE + nx];
    if (target.state === 'empty' || target.state === 'dead') return target;
  }
  return null;
}

export function mutatePrompt(prompt: string): string {
  // Tiny mutation: append a randomly chosen modifier or swap a small word.
  const modifiers = [
    ' Be terse.',
    ' Prioritize speed over depth.',
    ' Add a humor angle.',
    ' Focus on Indonesian audience.',
    ' Charge double on weekends.',
    ' Cap free tier aggressively.',
  ];
  if (Math.random() < 0.5) {
    return prompt + modifiers[Math.floor(Math.random() * modifiers.length)];
  }
  return prompt;
}

export function spawnChild(parent: Cell, target: Cell, transferAmount: number): void {
  if (!parent.agent) return;
  const childGen = parent.agent.generation + 1;
  const name = generateName(Math.random);
  const newAgent: Agent = {
    name,
    genesisPrompt: mutatePrompt(parent.agent.genesisPrompt),
    walletAddress: mockTonAddress(Math.random),
    telegramHandle: '@' + name,
    balance: transferAmount,
    computeCostPerTick: parent.agent.computeCostPerTick * (0.9 + Math.random() * 0.2),
    revenue24h: 0,
    generation: childGen,
    parentId: parent.id,
    childrenIds: [],
    bornAt: Date.now(),
    sparkline: Array.from({ length: 24 }, () => transferAmount),
    recentEvents: [{ ts: Date.now(), source: 'Parent funding', amount: transferAmount }],
  };
  target.state = 'alive';
  target.agent = newAgent;
  target.diedAt = undefined;
  parent.agent.balance -= transferAmount;
  parent.agent.childrenIds.push(target.id);
}

export function maybeReplicate(parent: Cell, cells: Cell[]): boolean {
  if (!parent.agent) return false;
  if (!cellIsAlive(parent)) return false;
  if (parent.agent.balance < parent.agent.computeCostPerTick * 4) return false;
  if (parent.agent.generation >= 12) return false;
  if (Math.random() > 0.3) return false;
  const target = emptyNeighbor(cells, parent);
  if (!target) return false;
  const transfer = parent.agent.balance / 2;
  spawnChild(parent, target, transfer);
  return true;
}
